// Theme-native footer signup (snippets/footer-signup-form.liquid). Subscribes to a Klaviyo list through Klaviyo's
// public client API, so the form and its translations live in the theme instead of one Klaviyo form per language.
(() => {
  if (customElements.get('footer-signup-form')) return;

  const API_REVISION = '2024-10-15';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

  class FooterSignupForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.errorElement = this.querySelector('.footer-signup__error');
      this.successElement = this.querySelector('.footer-signup__success');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.querySelector('[data-code]')?.addEventListener('click', (event) => this.copyCode(event.currentTarget));
      // The theme may move the popover elsewhere in the page (e.g. a bottom sheet on mobile), so find it by ID
      this.countryButton = this.querySelector('.footer-signup__country');
      this.countryPopover = this.countryButton && document.getElementById(this.countryButton.getAttribute('aria-controls'));
      this.countryPopover?.addEventListener('listbox:change', (event) => this.onCountryChange(event.target));
    }

    // Country picker: show the chosen flag and calling code on the button, then close the list
    onCountryChange(option) {
      const button = this.countryButton;
      const flag = button.querySelector('img');
      const optionFlag = option.querySelector('img');
      if (flag && optionFlag) {
        flag.src = optionFlag.currentSrc || optionFlag.src;
        flag.removeAttribute('srcset');
      }
      button.querySelector('[data-calling-code]').textContent = `+${option.dataset.callingCode}`;
      this.countryPopover.hide();
      this.form.elements.phone?.focus();
    }

    get callingCode() {
      return this.countryPopover?.querySelector('[role="option"][aria-selected="true"]')?.dataset.callingCode;
    }

    normalizePhone(value) {
      let phone = value.replace(/[\s().-]/g, '');
      if (!phone) return '';
      if (phone.startsWith('00')) phone = `+${phone.slice(2)}`;
      if (!phone.startsWith('+')) {
        // A number typed without "+" uses the picker's calling code; drop the national trunk prefix (leading 0)
        const code = this.callingCode;
        if (!code) return null;
        phone = `+${code}${phone.replace(/^0+/, '')}`;
      }
      return E164_PATTERN.test(phone) ? phone : null;
    }

    showError(message, field) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
      this.form.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus();
      }
    }

    // Klaviyo's client endpoint records email consent, plus SMS consent when a phone number is sent
    async subscribe(email, phone) {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: API_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.dataset.source || 'Website Footer',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    ...(phone && { phone_number: phone }),
                    properties: {
                      'Storefront Language': this.dataset.language,
                      'Storefront Country': this.dataset.country
                    }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
          }
        })
      });
      return response.ok;
    }

    async onSubmit(event) {
      event.preventDefault();
      const emailInput = this.form.elements.email;
      const phoneInput = this.form.elements.phone;
      const email = emailInput.value.trim();

      if (!email) return this.showError(this.messages.emailRequired, emailInput);
      if (!EMAIL_PATTERN.test(email)) return this.showError(this.messages.emailInvalid, emailInput);

      const phone = phoneInput ? this.normalizePhone(phoneInput.value) : '';
      if (phone === null) return this.showError(this.messages.phoneInvalid, phoneInput);

      this.showError('');
      const buttonContent = this.submitButton.innerHTML;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.textContent = this.messages.submitting;

      try {
        // Klaviyo rejects the whole request if it can't accept the number (e.g. SMS unsupported in that country),
        // so fall back to email-only rather than losing the signup
        const subscribed = (await this.subscribe(email, phone)) || (phone && (await this.subscribe(email, '')));
        if (!subscribed) throw new Error('Klaviyo subscription failed');

        this.recordSignup(email).catch(() => console.warn('Footer signup history could not be recorded'));

        try { window.klaviyo?.identify?.({ email }); } catch (error) { /* tracking is optional */ }
        this.form.hidden = true;
        this.successElement.hidden = false;
        this.successElement.focus();
      } catch (error) {
        this.showError(this.messages.generic);
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.innerHTML = buttonContent;
      }
    }

    async recordSignup(email) {
      const source = this.dataset.source || 'Website Footer';
      const profile = { data: { type: 'profile', attributes: { email, properties: {
        'Signup Source': source,
        'Submitted Footer Signup': true,
        'Footer Signup Last Submitted At': new Date().toISOString(),
        'Storefront Language': this.dataset.language,
        'Storefront Country': this.dataset.country
      } } } };
      const post = async (endpoint, data) => {
        const response = await fetch(`https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: API_REVISION },
          body: JSON.stringify({ data }), signal: AbortSignal.timeout(12000), keepalive: true
        });
        if (!response.ok) throw new Error('Klaviyo history request failed');
      };
      await Promise.all([
        post('profiles', { ...profile.data, meta: { patch_properties: { append: { 'Signup Sources': [source] } } } }),
        post('events', { type: 'event', attributes: {
          unique_id: crypto.randomUUID(),
          metric: { data: { type: 'metric', attributes: { name: 'Footer Signup Submitted' } } },
          profile,
          properties: {
            'Signup Source': source,
            'Marketing Opt In': true,
            'Marketing Consent Method': 'form_submission',
            'Marketing Consent Text': this.querySelector('.footer-signup__disclosure').textContent.trim()
          }
        } })
      ]);
    }

    async copyCode(button) {
      try {
        await navigator.clipboard.writeText(button.dataset.code);
        this.querySelector('.footer-signup__copied').hidden = false;
      } catch (error) {
        const range = document.createRange();
        range.selectNodeContents(button.querySelector('span'));
        getSelection().removeAllRanges();
        getSelection().addRange(range);
      }
    }
  }

  customElements.define('footer-signup-form', FooterSignupForm);
})();
